import React, { useEffect, useState } from 'react';
import type { Filters } from '../types/biApp';
import { api, mutate } from '../shared/api';
import { formatMoney, formatNumber } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { NiceLineChart, Treemap } from '../shared/charts';
import { Crumbs, Tabs } from '../shared/ui';
import { readUrlParam, useUrlSync } from '../shared/urlState';

type Locale = 'hr' | 'en';

type SpendByMonth = { period_month: string; total: string | number };
type SpendByChannel = { kanal: string; total: string | number };

type BudgetPlanerPayload = {
    djelatnost: string;
    target_ratio: number;
    cohort_year: number;
    cohort_stats: null | {
        cohort_year: number; n: number; mean_ltv: number; median_ltv: number;
        one_timer_n: number; one_timer_pct: number; one_timer_avg: number;
        repeater_n: number; repeater_avg: number; repeater_vs_one_timer_x: number;
        top5_pct_of_total: number; churn_24m_pct: number;
    };
    spend_by_month: SpendByMonth[];
    spend_by_channel: SpendByChannel[];
    metrics: {
        spend_3m: number; spend_12m: number;
        new_clients_3m: number; new_clients_12m: number;
        avg_new_per_month: number;
        cac_3m: number; cac_12m: number;
        mature_ltv_ruc: number;
        max_cac_at_target: number;
        current_ltv_cac_ratio: number;
    };
    recommendation: {
        recommended_new_clients: number;
        recommended_budget_eur: number;
        verdict: 'OK' | 'OVERSPENDING';
        verdict_msg: string;
    };
    current_split: Array<{ function: string; pct: number; amount: number }>;
    recommended_split: Array<{ function: string; pct: number; amount: number }>;
    scenarios: Array<{ ratio: number; max_cac_eur: number; budget_for_avg_clients: number; budget_for_double_clients: number }>;
};

type MarketingTab = 'budget' | 'whales' | 'cohort' | 'mix';

export function MarketingModule({ token, locale }: { token: string; locale: Locale; filters: Filters }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';
    const [tab, setTab] = useState<MarketingTab>(readUrlParam('tab', 'budget') as MarketingTab);
    useUrlSync('tab', tab, tab === 'budget');
    const [djelatnost, setDjelatnost] = useState<string>('investicijsko');
    const [targetRatio, setTargetRatio] = useState<number>(3.0);
    const [overrideClients, setOverrideClients] = useState<string>('');
    const [cohortYear, setCohortYear] = useState<number>(2024);
    const [data, setData] = useState<BudgetPlanerPayload | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (tab !== 'budget') return;
        setLoading(true); setError('');
        const qs = new URLSearchParams({ djelatnost, target_ratio: String(targetRatio), cohort_year: String(cohortYear) });
        if (overrideClients && /^\d+$/.test(overrideClients)) qs.set('target_new_clients', overrideClients);
        api<BudgetPlanerPayload>('/marketing/budget-planer?'+qs.toString(), token)
            .then(setData)
            .catch((e) => setError(e instanceof Error ? e.message : 'API'))
            .finally(() => setLoading(false));
    }, [token, djelatnost, targetRatio, overrideClients, cohortYear, tab]);

    return (
        <section className="ado-m1" style={{ padding: '14px 18px' }}>
            {isHrms ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginBottom: 16 }}>
                    <Crumbs items={[t('Dashboard', 'Dashboard'), t('Marketing', 'Marketing')]} />
                    <Tabs
                        tabs={[
                            { key: 'budget', label: t('Budget Planer', 'Budget Planner'), icon: '💰' },
                            { key: 'whales', label: t('Whales (KAM)', 'Whales (KAM)'), icon: '🐋' },
                            { key: 'cohort', label: t('Cohort heatmap', 'Cohort heatmap'), icon: '📊' },
                            { key: 'mix', label: t('Mix proizvoda', 'Product mix'), icon: '🎁' },
                        ]}
                        active={tab}
                        onChange={(k) => setTab(k as MarketingTab)}
                    />
                </div>
            ) : (
            <>
            <header style={{ marginBottom: 12 }}>
                <h1 style={{ margin: 0, fontSize: '1.2rem' }}>🎯 {t('Marketing', 'Marketing')}</h1>
            </header>
            {/* Tabs */}
            <nav style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.3)' }}>
                {([
                    ['budget', '💰 '+t('Budget Planer', 'Budget Planner')],
                    ['whales', '🐋 '+t('Whales (KAM)', 'Whales (KAM)')],
                    ['cohort', '📊 '+t('Cohort heatmap', 'Cohort heatmap')],
                    ['mix', '🎁 '+t('Mix proizvoda', 'Product mix')],
                ] as Array<[MarketingTab, string]>).map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setTab(key)} style={{
                        padding: '6px 14px', border: 0, borderBottom: tab === key ? '2px solid var(--ad-acc)' : '2px solid transparent',
                        background: 'transparent', cursor: 'pointer', fontSize: '0.86rem',
                        color: tab === key ? 'var(--ad-ink)' : '#888', fontWeight: tab === key ? 700 : 500,
                    }}>{label}</button>
                ))}
            </nav>
            </>
            )}

            {tab === 'whales' ? <WhalesPanel token={token} locale={locale} /> : null}
            {tab === 'cohort' ? <CohortHeatmapPanel token={token} locale={locale} /> : null}
            {tab === 'mix' ? <MixProizvodaPanel token={token} locale={locale} /> : null}
            {tab !== 'budget' ? null : <BudgetPlanerView
                data={data} loading={loading} error={error}
                djelatnost={djelatnost} setDjelatnost={setDjelatnost}
                targetRatio={targetRatio} setTargetRatio={setTargetRatio}
                overrideClients={overrideClients} setOverrideClients={setOverrideClients}
                cohortYear={cohortYear} setCohortYear={setCohortYear}
                locale={locale}
            />}
        </section>
    );
}

function BudgetPlanerView({
    data, loading, error, djelatnost, setDjelatnost, targetRatio, setTargetRatio,
    overrideClients, setOverrideClients, cohortYear, setCohortYear, locale,
}: {
    data: BudgetPlanerPayload | null; loading: boolean; error: string;
    djelatnost: string; setDjelatnost: (v: string) => void;
    targetRatio: number; setTargetRatio: (v: number) => void;
    overrideClients: string; setOverrideClients: (v: string) => void;
    cohortYear: number; setCohortYear: (v: number) => void;
    locale: Locale;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    if (loading && !data) return <div style={{ padding: 20 }}><BrandSpinner /></div>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    const m = data.metrics;
    const verdictColor = data.recommendation.verdict === 'OVERSPENDING' ? '#b14040' : '#1f7a36';

    return (
        <div>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.82rem' }}>
                {t('Automatska preporuka mjesečnog budgeta na osnovu trailing 3M spend × CAC × cilja LTV:CAC.', 'Auto monthly budget recommendation: trailing 3M spend × CAC × LTV:CAC target.')}
            </p>

            {/* Controls */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', padding: '10px 12px', background: 'rgba(var(--ad-soft-rgb),0.08)', borderRadius: 6, marginBottom: 12, fontSize: '0.84rem' }}>
                <label>
                    {t('Djelatnost:', 'Function:')}{' '}
                    <select value={djelatnost} onChange={(e) => setDjelatnost(e.target.value)} style={{ padding: '4px 8px' }}>
                        <option value="investicijsko">Investicijsko zlato</option>
                        <option value="otkup">Otkup zlata</option>
                        <option value="zalog">Zalog</option>
                    </select>
                </label>
                <label>
                    {t('Cilj LTV:CAC:', 'Target LTV:CAC:')}{' '}
                    <select value={String(targetRatio)} onChange={(e) => setTargetRatio(parseFloat(e.target.value))} style={{ padding: '4px 8px' }}>
                        <option value="2">2:1 (agresivni rast)</option>
                        <option value="2.5">2.5:1</option>
                        <option value="3">3:1 (standard)</option>
                        <option value="4">4:1 (konzervativno)</option>
                        <option value="5">5:1</option>
                    </select>
                </label>
                <label>
                    {t('Ručno cilj novih klijenata/mj:', 'Override new clients/mo:')}{' '}
                    <input type="number" min="0" placeholder={String(m.avg_new_per_month)} value={overrideClients} onChange={(e) => setOverrideClients(e.target.value)} style={{ padding: '4px 8px', width: 90 }} />
                </label>
                <label>
                    {t('Cohort godina:', 'Cohort year:')}{' '}
                    <select value={String(cohortYear)} onChange={(e) => setCohortYear(parseInt(e.target.value, 10))} style={{ padding: '4px 8px' }}>
                        {[2022, 2023, 2024].map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </label>
            </div>

            {/* Cohort stats panel */}
            {data.cohort_stats ? (
                <div style={{ padding: '12px 14px', background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 4, marginBottom: 12 }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: '0.95rem', color: 'var(--ad-ink)' }}>
                        💎 {t(`Mature cohort ${data.cohort_stats.cohort_year} — ${formatNumber(data.cohort_stats.n, locale)} klijenata`, `Mature cohort ${data.cohort_stats.cohort_year} — ${formatNumber(data.cohort_stats.n, locale)} clients`)}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                        <Kpi label="Mean LTV" value={formatMoney(data.cohort_stats.mean_ltv, locale)} sub={`median ${formatMoney(data.cohort_stats.median_ltv, locale)}`} />
                        <Kpi label={t('One-timers', 'One-timers')} value={`${data.cohort_stats.one_timer_pct}%`} sub={`avg ${formatMoney(data.cohort_stats.one_timer_avg, locale)}`} />
                        <Kpi label={t('Repeaters', 'Repeaters')} value={formatNumber(data.cohort_stats.repeater_n, locale)} sub={`avg ${formatMoney(data.cohort_stats.repeater_avg, locale)}`} />
                        <Kpi label={t('Whale vs one-timer', 'Whale vs one-timer')} value={`${data.cohort_stats.repeater_vs_one_timer_x}×`} sub={t('LTV ratio', 'LTV ratio')} accent="pos" />
                        <Kpi label={t('Top 5% drže', 'Top 5% hold')} value={`${data.cohort_stats.top5_pct_of_total}%`} sub={t('RUC', 'RUC')} accent={data.cohort_stats.top5_pct_of_total > 80 ? 'neg' : undefined} />
                        <Kpi label={t('Churn 24m', 'Churn 24m')} value={`${data.cohort_stats.churn_24m_pct}%`} sub={t('neaktivni', 'inactive')} accent={data.cohort_stats.churn_24m_pct > 50 ? 'neg' : undefined} />
                    </div>
                </div>
            ) : null}

            {/* Verdict banner */}
            <div style={{ padding: '12px 14px', background: data.recommendation.verdict === 'OVERSPENDING' ? 'rgba(177,64,64,0.10)' : 'rgba(31,122,54,0.10)', borderLeft: `4px solid ${verdictColor}`, borderRadius: 4, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: verdictColor, fontSize: '0.95rem' }}>
                    {data.recommendation.verdict === 'OVERSPENDING' ? '⚠ ' : '✓ '}
                    {data.recommendation.verdict}: {t('preporučeni budget za sljedeći mjesec', 'recommended budget for next month')}: <strong>{formatMoney(data.recommendation.recommended_budget_eur, locale)}</strong>
                </div>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>{data.recommendation.verdict_msg}</p>
            </div>

            {/* KPI grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 16 }}>
                <Kpi label={t('Spend 3M', 'Spend 3M')} value={formatMoney(m.spend_3m, locale)} sub={`${formatMoney(m.spend_3m/3, locale)} /mj`} />
                <Kpi label={t('Spend 12M', 'Spend 12M')} value={formatMoney(m.spend_12m, locale)} sub={`${formatMoney(m.spend_12m/12, locale)} /mj`} />
                <Kpi label={t('Novi 3M', 'New 3M')} value={formatNumber(m.new_clients_3m, locale)} sub={`${m.avg_new_per_month}/mj`} />
                <Kpi label={t('CAC 3M', 'CAC 3M')} value={formatMoney(m.cac_3m, locale)} accent={m.cac_3m > m.max_cac_at_target ? 'neg' : 'pos'} sub={`vs cilj ${formatMoney(m.max_cac_at_target, locale)}`} />
                <Kpi label="LTV" value={formatMoney(m.mature_ltv_ruc, locale)} sub="mature RUC" />
                <Kpi label="LTV:CAC" value={`${m.current_ltv_cac_ratio}:1`} accent={m.current_ltv_cac_ratio >= targetRatio ? 'pos' : 'neg'} sub={`cilj ${targetRatio}:1`} />
            </div>

            {/* Scenario simulator */}
            <section style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '0.95rem', margin: '0 0 8px' }}>📊 {t('Scenario simulator', 'Scenario simulator')}</h3>
                <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                    <thead>
                        <tr>
                            <th>LTV:CAC</th>
                            <th>{t('Max CAC', 'Max CAC')}</th>
                            <th>{t(`Budget za ${m.avg_new_per_month} novih (avg)`, `Budget for ${m.avg_new_per_month} new (avg)`)}</th>
                            <th>{t(`Budget za 2× novih (${m.avg_new_per_month*2})`, `Budget for 2× new (${m.avg_new_per_month*2})`)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.scenarios.map((s) => (
                            <tr key={s.ratio} style={{ background: Math.abs(s.ratio - targetRatio) < 0.01 ? 'rgba(31,122,54,0.08)' : undefined }}>
                                <td><strong>{s.ratio}:1</strong>{Math.abs(s.ratio - targetRatio) < 0.01 ? ' ← aktivno' : ''}</td>
                                <td>{formatMoney(s.max_cac_eur, locale)}</td>
                                <td>{formatMoney(s.budget_for_avg_clients, locale)}</td>
                                <td>{formatMoney(s.budget_for_double_clients, locale)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            {/* Split usporedba */}
            <section style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <SplitTable title={`📉 ${t('Sadašnji split (12M)', 'Current split (12M)')}`} rows={data.current_split} locale={locale} />
                <SplitTable title={`📈 ${t('Preporučeni split (next mj)', 'Recommended split (next mo)')}`} rows={data.recommended_split} locale={locale} accent="#1f7a36" />
            </section>

            {/* Spend by month line chart (visual!) */}
            <section style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '0.95rem', margin: '0 0 8px' }}>📈 {t('Mjesečni spend (zadnjih 12M)', 'Monthly spend (last 12M)')}</h3>
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, padding: 12 }}>
                    <NiceLineChart
                        series={[{
                            name: t('Trošak', 'Spend'),
                            color: 'var(--ad-acc)',
                            values: [...data.spend_by_month].reverse().map((r) => Number(r.total)),
                        }]}
                        height={200}
                        locale={locale}
                        xLabels={[...data.spend_by_month].reverse().map((r) => String(r.period_month).slice(0, 7))}
                        yFormatter={(n) => n >= 1e3 ? (n/1e3).toFixed(0)+'k €' : Math.round(n)+' €'}
                    />
                </div>
            </section>

            {/* Spend by channel — TREEMAP */}
            <section>
                <h3 style={{ fontSize: '0.95rem', margin: '0 0 8px' }}>🟧 {t('Trošak po kanalu (zadnjih 3M)', 'Spend by channel (last 3M)')}</h3>
                <Treemap
                    items={data.spend_by_channel.map((r) => ({ label: r.kanal, value: Number(r.total) }))}
                    height={200}
                    locale={locale}
                    moneyFormatter={(n) => formatMoney(n, locale)}
                />
            </section>
        </div>
    );
}

function WhalesPanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type Whale = {
        customer_code: string; customer_name: string; invoice_count: number; total_eur: number;
        first_doc: string; last_doc: string; recency_days: number | null; segment: string;
        kam_user_id: number | null; kam_notes: string | null; last_contact_at: string | null;
    };
    const [whales, setWhales] = useState<Whale[]>([]);
    const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);
    const [loading, setLoading] = useState(false);
    const [limit] = useState(100);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api<{ whales: Whale[] }>(`/marketing/whales?limit=${limit}`, token),
            api<{ users: Array<{ id: number; name: string }> }>('/marketing/kam-users', token),
        ]).then(([w, u]) => { setWhales(w.whales); setUsers(u.users); })
            .finally(() => setLoading(false));
    }, [token, limit, reloadKey]);

    if (loading && whales.length === 0) return <div style={{ padding: 20 }}><BrandSpinner /></div>;

    const segColor = (seg: string) => seg === 'active' ? '#1f7a36' : seg === 'at_risk' ? 'var(--ad-acc)' : seg === 'lost' ? '#b14040' : '#888';
    const segLabel = (seg: string) => seg === 'active' ? t('Aktivan', 'Active') : seg === 'at_risk' ? t('At-risk', 'At-risk') : seg === 'lost' ? t('Izgubljen', 'Lost') : seg;

    async function assignKam(customerCode: string, kamUserId: number | null) {
        try {
            await mutate(`/marketing/whales/${customerCode}/kam`, token, 'POST', { kam_user_id: kamUserId });
            setReloadKey((k) => k + 1);
        } catch (e) { alert(e instanceof Error ? e.message : 'fail'); }
    }
    async function markContacted(customerCode: string) {
        try {
            await mutate(`/marketing/whales/${customerCode}/kam`, token, 'POST', { mark_contacted: true });
            setReloadKey((k) => k + 1);
        } catch { /* ignore */ }
    }

    return (
        <div>
            <p className="muted" style={{ margin: '0 0 8px', fontSize: '0.82rem' }}>
                {t(`Top ${limit} kupaca po ukupnom prometu (ADO IRA, 2025+). Dodijeli KAM-a i markiraj kontakt.`, `Top ${limit} customers by revenue (ADO IRA, 2025+). Assign KAM and mark contact.`)}
            </p>
            <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{t('Kupac', 'Customer')}</th>
                        <th>{t('Segment', 'Segment')}</th>
                        <th style={{ textAlign: 'right' }}>{t('Promet', 'Revenue')}</th>
                        <th>{t('Računa', 'Invoices')}</th>
                        <th>{t('Last', 'Last')}</th>
                        <th>{t('KAM', 'KAM')}</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {whales.map((w, i) => (
                        <tr key={w.customer_code}>
                            <td>{i + 1}</td>
                            <td>
                                <strong>{w.customer_name}</strong>
                                <br /><span className="muted" style={{ fontSize: '0.74rem' }}>{w.customer_code}</span>
                            </td>
                            <td><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', background: segColor(w.segment)+'22', color: segColor(w.segment), fontWeight: 700 }}>{segLabel(w.segment)}</span></td>
                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(w.total_eur, locale)}</td>
                            <td>{w.invoice_count}</td>
                            <td>{w.last_doc}{w.recency_days != null ? <span className="muted" style={{ fontSize: '0.7rem' }}> ({w.recency_days}d)</span> : null}</td>
                            <td>
                                <select value={w.kam_user_id ?? ''} onChange={(e) => void assignKam(w.customer_code, e.target.value ? parseInt(e.target.value, 10) : null)} style={{ padding: '2px 6px', fontSize: '0.78rem' }}>
                                    <option value="">— {t('nedodijeljen', 'unassigned')} —</option>
                                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </td>
                            <td>
                                <button type="button" onClick={() => void markContacted(w.customer_code)} title={t('Markiraj kontakt', 'Mark contact')} style={{ padding: '2px 8px', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fff', borderRadius: 3, cursor: 'pointer', fontSize: '0.78rem' }}>✓ {t('kontakt', 'contact')}</button>
                                {w.last_contact_at ? <span className="muted" style={{ marginLeft: 4, fontSize: '0.7rem' }}>· {String(w.last_contact_at).slice(0, 10)}</span> : null}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CohortHeatmapPanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type Cell = { offset: number; n: number; pct: number };
    type Cohort = { cohort: string; size: number; cells: Cell[] };
    const [byYear, setByYear] = useState(true);
    const [data, setData] = useState<{ cohorts: Cohort[]; max_offset: number } | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        api<{ cohorts: Cohort[]; max_offset: number }>(`/marketing/cohort-heatmap${byYear ? '?by_year=1' : ''}`, token)
            .then(setData).finally(() => setLoading(false));
    }, [token, byYear]);

    if (loading && !data) return <div style={{ padding: 20 }}><BrandSpinner /></div>;
    if (!data) return null;

    const heat = (pct: number) => {
        if (pct <= 0) return '#fff';
        const intensity = Math.min(1, pct / 100);
        const r = Math.round(255 - intensity * 70);
        const g = Math.round(255 - intensity * 100);
        const b = Math.round(255 - intensity * 220);
        return `rgb(${r},${g},${b})`;
    };

    return (
        <div>
            <div style={{ marginBottom: 10, fontSize: '0.82rem' }}>
                <label>
                    <input type="checkbox" checked={byYear} onChange={(e) => setByYear(e.target.checked)} /> {t('Grupiraj po godini (umjesto mjesec)', 'Group by year (instead of month)')}
                </label>
            </div>
            <p className="muted" style={{ fontSize: '0.78rem' }}>
                {t('Postotak kupaca iz kohorte koji su aktivni N mjeseci nakon prve kupnje. Tamnija = veće retention.', 'Percentage of cohort still active N months after first purchase. Darker = higher retention.')}
            </p>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ fontSize: '0.72rem', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ padding: '4px 8px', textAlign: 'left' }}>{t('Kohorta', 'Cohort')}</th>
                            <th style={{ padding: '4px 8px' }}>N</th>
                            {Array.from({ length: data.max_offset + 1 }, (_, i) => <th key={i} style={{ padding: '4px 6px', minWidth: 36 }}>{byYear ? `+${i}m` : `M${i}`}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {data.cohorts.map((c) => (
                            <tr key={c.cohort}>
                                <td style={{ padding: '4px 8px', fontWeight: 700 }}>{c.cohort}</td>
                                <td style={{ padding: '4px 8px' }}>{formatNumber(c.size, locale)}</td>
                                {c.cells.map((cell) => (
                                    <td key={cell.offset} style={{ padding: '4px 4px', background: heat(cell.pct), textAlign: 'center', fontWeight: cell.pct > 50 ? 700 : 400 }} title={`${cell.n} klijenata (${cell.pct}%)`}>
                                        {cell.pct > 0 ? cell.pct+'%' : ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function MixProizvodaPanel({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    type Row = { offers: number; kom: number; eur: number };
    type TopProduct = {
        sifra_artikla: string; naziv: string; offers: number; kom: number; fine_g: number; eur: number;
        historic_ruc_eur: number; historic_cost_eur: number; historic_ruc_pct: number; ruc_coverage: number;
        projected_ruc_per_kom: number | null; projected_ruc_total: number | null;
        sale_per_kom: number | null; cost_per_kom: number | null;
        pricelist_found: boolean;
    };
    type Payload = {
        since_days: number; date_from: string; date_to: string; segment: string; premium_override: number | null;
        spot_eur_per_g: number | null; pricelist_effective: string | null;
        by_metal: Array<{ vrsta_kovine: string | null } & Row & { fine_g: number }>;
        by_mint: Array<{ mint: string } & Row & { fine_g: number }>;
        by_size: Array<{ size: string } & Row>;
        by_type: Array<{ type: string } & Row>;
        top_products: TopProduct[];
    };
    const [data, setData] = useState<Payload | null>(null);
    const today = new Date().toISOString().slice(0, 10);
    const [sinceDays, setSinceDays] = useState(90);
    const [useCustom, setUseCustom] = useState(false);
    const [dateFrom, setDateFrom] = useState<string>(today);
    const [dateTo, setDateTo] = useState<string>(today);
    const [segment, setSegment] = useState<string>('poslovnice');
    const [premiumOverride, setPremiumOverride] = useState<string>('');

    useEffect(() => {
        const qs = new URLSearchParams({ segment });
        if (useCustom && dateFrom && dateTo) { qs.set('date_from', dateFrom); qs.set('date_to', dateTo); }
        else qs.set('since_days', String(sinceDays));
        if (premiumOverride && /^\d+(\.\d+)?$/.test(premiumOverride)) qs.set('premium_pct_override', premiumOverride);
        api<Payload>('/marketing/mix-proizvoda?'+qs.toString(), token).then(setData);
    }, [token, sinceDays, segment, premiumOverride, useCustom, dateFrom, dateTo]);

    // Quick range presets (when not in custom mode)
    function applyPreset(key: string) {
        const now = new Date();
        const toDate = now.toISOString().slice(0, 10);
        const startOf = (d: Date) => d.toISOString().slice(0, 10);
        let fromDate = toDate;
        switch (key) {
            case 'today': fromDate = toDate; break;
            case 'yesterday': {
                const d = new Date(now); d.setDate(d.getDate() - 1);
                fromDate = startOf(d); break;
            }
            case 'this_week': {
                const d = new Date(now); const day = d.getDay() || 7;
                d.setDate(d.getDate() - day + 1); fromDate = startOf(d); break;
            }
            case 'this_month': {
                const d = new Date(now.getFullYear(), now.getMonth(), 1); fromDate = startOf(d); break;
            }
            case 'this_quarter': {
                const q = Math.floor(now.getMonth() / 3);
                const d = new Date(now.getFullYear(), q * 3, 1); fromDate = startOf(d); break;
            }
            case 'ytd': fromDate = `${now.getFullYear()}-01-01`; break;
            case 'last_year': {
                fromDate = `${now.getFullYear() - 1}-01-01`;
                setUseCustom(true); setDateFrom(fromDate); setDateTo(`${now.getFullYear() - 1}-12-31`);
                return;
            }
        }
        setUseCustom(true); setDateFrom(fromDate); setDateTo(toDate);
    }

    if (!data) return <div style={{ padding: 20 }}><BrandSpinner /></div>;

    return (
        <div>
            <div style={{ marginBottom: 12 }}>
                <p className="muted" style={{ margin: '0 0 8px', fontSize: '0.82rem' }}>
                    {t(`Mix proizvoda — iz prihvaćenih ponuda. Izvor: live POS feed (bi_gold_daily_offers).`, `Product mix — accepted offers. Source: live POS feed.`)}
                </p>

                {/* Quick range presets */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.78rem' }}>
                    <span style={{ color: 'var(--ad-ink)', fontWeight: 700, marginRight: 4 }}>📅 {t('Brzi raspon:', 'Quick range:')}</span>
                    {([
                        ['today', t('Danas', 'Today')],
                        ['yesterday', t('Jučer', 'Yesterday')],
                        ['this_week', t('Ovaj tjedan', 'This week')],
                        ['this_month', t('Ovaj mjesec', 'This month')],
                        ['this_quarter', t('Kvartal', 'Quarter')],
                        ['ytd', 'YTD'],
                        ['last_year', t('Prošla godina', 'Last year')],
                    ] as const).map(([key, label]) => (
                        <button key={key} type="button" onClick={() => applyPreset(key)}
                            style={{ padding: '3px 10px', borderRadius: 4, fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fff', color: 'var(--ad-ink)' }}>
                            {label}
                        </button>
                    ))}
                    {([30, 90, 180, 365] as const).map((d) => (
                        <button key={d} type="button" onClick={() => { setUseCustom(false); setSinceDays(d); }}
                            style={{ padding: '3px 10px', borderRadius: 4, fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer',
                                border: ! useCustom && sinceDays === d ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                background: ! useCustom && sinceDays === d ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : '#fff',
                                color: ! useCustom && sinceDays === d ? '#fffbeb' : 'var(--ad-ink)' }}>
                            {d}d
                        </button>
                    ))}
                </div>

                {/* Custom range + segment + premium override */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.82rem' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={useCustom} onChange={(e) => setUseCustom(e.target.checked)} />
                        {t('Custom raspon:', 'Custom range:')}
                    </label>
                    <label>
                        {t('od', 'from')}
                        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setUseCustom(true); }} style={{ padding: '4px 8px', marginLeft: 4 }} />
                    </label>
                    <label>
                        {t('do', 'to')}
                        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setUseCustom(true); }} style={{ padding: '4px 8px', marginLeft: 4 }} />
                    </label>
                    <span style={{ width: 1, height: 20, background: 'rgba(var(--ad-soft-rgb),0.4)' }} />
                    <label>
                        {t('Cjenik (segment):', 'Pricelist:')}{' '}
                        <select value={segment} onChange={(e) => setSegment(e.target.value)} style={{ padding: '4px 8px' }}>
                            <option value="poslovnice">Poslovnice (retail)</option>
                            <option value="web_registered">Web registered</option>
                            <option value="web_guest">Web guest</option>
                            <option value="slovenija">Slovenija</option>
                            <option value="bugarska">Bugarska</option>
                        </select>
                    </label>
                    <label>
                        {t('Simulacija premium %:', 'Simulation premium %:')}{' '}
                        <input type="number" step="0.5" placeholder={t('default', 'default')} value={premiumOverride} onChange={(e) => setPremiumOverride(e.target.value)} style={{ padding: '4px 8px', width: 80 }} />
                    </label>
                </div>
            </div>

            <div style={{ padding: '6px 12px', background: 'rgba(var(--ad-soft-rgb),0.08)', borderLeft: '3px solid var(--ad-acc)', borderRadius: 4, marginBottom: 12, fontSize: '0.78rem' }}>
                💡 {t(`Aktivni raspon: ${data.date_from} → ${data.date_to} (${data.since_days} dana)`, `Active range: ${data.date_from} → ${data.date_to} (${data.since_days} days)`)}
                {data.spot_eur_per_g !== null ? ` · ${t('Spot zlato', 'Gold spot')}: ${data.spot_eur_per_g} €/g · ${t('Cjenik', 'Pricelist')}: ${data.pricelist_effective ?? '—'} · ${t('Segment', 'Segment')}: ${data.segment}` : ''}
                {data.premium_override !== null ? ` · OVERRIDE premium ${data.premium_override}%` : ''}
            </div>

            {/* Treemap: po veličini poluge (najvažnije za product mix) */}
            <section style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '0.92rem', margin: '0 0 6px' }}>📦 {t('Po veličini poluge (€ promet)', 'By bar size (€ revenue)')}</h3>
                <Treemap
                    items={data.by_size.map((r) => ({ label: r.size, value: r.eur }))}
                    height={200}
                    locale={locale}
                    moneyFormatter={(n) => formatMoney(n, locale)}
                />
            </section>

            {/* Two-column: by_mint + by_type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <section>
                    <h3 style={{ fontSize: '0.92rem', margin: '0 0 6px' }}>🏛 {t('Po kovnici', 'By mint')}</h3>
                    <Treemap
                        items={data.by_mint.map((r) => ({ label: r.mint, value: r.eur }))}
                        height={180}
                        locale={locale}
                        moneyFormatter={(n) => formatMoney(n, locale)}
                    />
                </section>
                <section>
                    <h3 style={{ fontSize: '0.92rem', margin: '0 0 6px' }}>🎯 {t('Po tipu', 'By type')}</h3>
                    <Treemap
                        items={data.by_type.map((r) => ({ label: r.type, value: r.eur }))}
                        height={180}
                        locale={locale}
                        moneyFormatter={(n) => formatMoney(n, locale)}
                    />
                </section>
            </div>

            {/* Detalj: po metalu + po veličini tablica */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <section>
                    <h3 style={{ fontSize: '0.92rem', margin: '0 0 6px' }}>{t('Po metalu', 'By metal')}</h3>
                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                        <thead><tr><th>{t('Metal', 'Metal')}</th><th>{t('Ponuda', 'Offers')}</th><th>kom</th><th>fine g</th><th style={{ textAlign: 'right' }}>€</th></tr></thead>
                        <tbody>
                            {data.by_metal.map((r) => (
                                <tr key={r.vrsta_kovine ?? 'na'}>
                                    <td><strong>{r.vrsta_kovine ?? '—'}</strong></td>
                                    <td>{r.offers}</td>
                                    <td>{r.kom}</td>
                                    <td>{formatNumber(r.fine_g, locale)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(r.eur, locale)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
                <section>
                    <h3 style={{ fontSize: '0.92rem', margin: '0 0 6px' }}>{t('Po veličini — tablica', 'By size — table')}</h3>
                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                        <thead><tr><th>{t('Veličina', 'Size')}</th><th>{t('Ponuda', 'Offers')}</th><th>kom</th><th style={{ textAlign: 'right' }}>€</th></tr></thead>
                        <tbody>
                            {data.by_size.map((r) => (
                                <tr key={r.size}>
                                    <td><strong>{r.size}</strong></td>
                                    <td>{r.offers}</td>
                                    <td>{r.kom}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(r.eur, locale)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            </div>

            {/* Top 20 individualnih proizvoda + RUC kalkulacije */}
            <section>
                <h3 style={{ fontSize: '0.92rem', margin: '0 0 6px' }}>🥇 {t('Top 20 proizvoda — promet, povijesni RUC, projicirani RUC', 'Top 20 — revenue, historic RUC, projected RUC')}</h3>
                <p className="muted" style={{ fontSize: '0.72rem', margin: '0 0 6px' }}>
                    {t('Povijesni RUC = sum(expected_ruc_eur) iz POS feed-a. Projicirani RUC = (sale − cost) × kom prema cjeniku m.40.1.', 'Historic RUC = sum from POS feed. Projected RUC = (sale − cost) × units per m.40.1 pricelist.')}
                </p>
                <div style={{ overflowX: 'auto' }}>
                    <table className="admin-user-table" style={{ fontSize: '0.78rem', minWidth: 1000 }}>
                        <thead><tr>
                            <th>#</th>
                            <th>{t('Šifra', 'SKU')}</th>
                            <th>{t('Artikl', 'Product')}</th>
                            <th>kom</th>
                            <th style={{ textAlign: 'right' }}>{t('Promet €', 'Revenue €')}</th>
                            <th style={{ textAlign: 'right' }} title="Per cjenik m.40.1">{t('Sale/kom', 'Sale/unit')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Cost/kom', 'Cost/unit')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Proj. RUC/kom', 'Proj RUC/unit')}</th>
                            <th style={{ textAlign: 'right' }} title="Per kom × kom prodanih">{t('Proj. UKUPNO', 'Proj TOTAL')}</th>
                            <th style={{ textAlign: 'right' }} title="iz POS feed expected_ruc_eur">{t('Povijesni RUC', 'Historic RUC')}</th>
                        </tr></thead>
                        <tbody>
                            {data.top_products.map((r, i) => {
                                const rucPct = r.projected_ruc_per_kom != null && r.sale_per_kom ? ((r.projected_ruc_per_kom / r.sale_per_kom) * 100).toFixed(1) : null;
                                return (
                                    <tr key={r.sifra_artikla}>
                                        <td>{i + 1}</td>
                                        <td><code>{r.sifra_artikla}</code></td>
                                        <td title={r.naziv} style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.naziv}</td>
                                        <td>{r.kom}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(r.eur, locale)}</td>
                                        <td style={{ textAlign: 'right' }}>{r.sale_per_kom != null ? formatMoney(r.sale_per_kom, locale) : <span className="muted">—</span>}</td>
                                        <td style={{ textAlign: 'right' }}>{r.cost_per_kom != null ? formatMoney(r.cost_per_kom, locale) : <span className="muted">—</span>}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: r.projected_ruc_per_kom == null ? '#999' : r.projected_ruc_per_kom >= 0 ? '#1f7a36' : '#b14040' }}>
                                            {r.projected_ruc_per_kom != null ? formatMoney(r.projected_ruc_per_kom, locale) : '—'}
                                            {rucPct ? <span className="muted" style={{ fontWeight: 400, fontSize: '0.7rem', marginLeft: 4 }}>({rucPct}%)</span> : null}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: r.projected_ruc_total == null ? '#999' : r.projected_ruc_total >= 0 ? '#1f7a36' : '#b14040' }}>
                                            {r.projected_ruc_total != null ? formatMoney(r.projected_ruc_total, locale) : '—'}
                                            {! r.pricelist_found ? <span className="muted" style={{ fontSize: '0.66rem', marginLeft: 4 }} title={t('Nema cjenika za ovaj proizvod', 'No pricelist for this product')}>⚠</span> : null}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {r.historic_ruc_eur !== 0 ? <>
                                                <strong style={{ color: r.historic_ruc_eur >= 0 ? '#1f7a36' : '#b14040' }}>{formatMoney(r.historic_ruc_eur, locale)}</strong>
                                                <span className="muted" style={{ fontWeight: 400, fontSize: '0.7rem', marginLeft: 4 }}>({r.historic_ruc_pct.toFixed(1)}%)</span>
                                            </> : <span className="muted">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: 'rgba(var(--ad-soft-rgb),0.10)', fontWeight: 700, borderTop: '2px solid var(--ad-acc)' }}>
                                <td colSpan={4} style={{ textAlign: 'right' }}>{t('UKUPNO top 20:', 'TOTAL top 20:')}</td>
                                <td style={{ textAlign: 'right' }}>{formatMoney(data.top_products.reduce((s, r) => s + r.eur, 0), locale)}</td>
                                <td colSpan={3} />
                                <td style={{ textAlign: 'right', color: '#1f7a36' }}>{formatMoney(data.top_products.reduce((s, r) => s + (r.projected_ruc_total ?? 0), 0), locale)}</td>
                                <td style={{ textAlign: 'right' }}>{formatMoney(data.top_products.reduce((s, r) => s + r.historic_ruc_eur, 0), locale)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </section>
        </div>
    );
}

function Kpi({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: 'pos' | 'neg' }) {
    const color = accent === 'pos' ? '#1f7a36' : accent === 'neg' ? '#b14040' : '#1a1a1a';
    return (
        <div style={{ padding: '8px 12px', background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 4 }}>
            <div style={{ fontSize: '0.66rem', color: '#8a8579', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            {sub ? <div style={{ fontSize: '0.7rem', color: '#999' }}>{sub}</div> : null}
        </div>
    );
}

function SplitTable({ title, rows, locale, accent }: { title: string; rows: Array<{ function: string; pct: number; amount: number }>; locale: Locale; accent?: string }) {
    return (
        <div style={{ padding: '10px 12px', background: '#fff', border: '1px solid '+(accent ?? 'rgba(var(--ad-soft-rgb),0.3)'), borderRadius: 4 }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '0.86rem', color: accent ?? 'var(--ad-ink)' }}>{title}</h4>
            <table style={{ width: '100%', fontSize: '0.82rem' }}>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.function}>
                            <td style={{ textTransform: 'capitalize' }}>{r.function}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.pct}%</td>
                            <td style={{ textAlign: 'right' }}>{formatMoney(r.amount, locale)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
